// src/pages/tasks/task-list-page/TaskListPage.tsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import CommonTableTwoLine from "../../../components/common/common-table/common-table-two-line";
import TaskListColumnModel from "./task-list-column-model";
import { ApiRequest } from "../../../services/api/api";
import {
  EyeOutlined,
  EditOutlined,
  NotificationOutlined,
  CheckCircleOutlined,
  CommentOutlined,
  DeleteOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { message, Spin, Modal, Radio, Space, Select, Input, Upload, Button, Typography } from "antd";
import moment from "moment-timezone";

const NZ_TIMEZONE = "Pacific/Auckland";
const toNZTime = (date: any) => date ? moment.utc(date).tz(NZ_TIMEZONE) : null;
import { useHistory } from "react-router-dom";
import { getUserId } from "../../../services/lib/utils/auth.utils";
import { normalizeTaskCommentDisplayType, taskCommentTypeForApi } from "../task-comment-utils";
import SweetAlertService from "../../../services/lib/utils/sweet-alert-service";
import { calculateStatus, Status, getStatusColor } from "../task-status-utils";
import TaskNotificationModal from "./TaskNotificationModal";

interface TaskParticipant {
  userId: number;
  name: string;
  role: string;
}

interface TaskCommentInfo {
  commentType: string;
  content: string;
  createdBy: string;
  createdAt: string;
}

// Mock数据开关 - 生产环境暂未部署Task Management功能
const USE_MOCK = false;

const FALLBACK_STATUS_COLOR_MAP: Record<string, string> = {
  "Not Started": "#8c8c8c",
  "In Progress": "#52c41a",
  Completed: "#000000",
  Overdue: "#f5222d",
};

const normalizeStatusLabel = (raw: any): string => {
  const value = String(raw ?? "").trim().toLowerCase();
  switch (value) {
    case "in progress":
    case "in_progress":
    case "in-progress":
      return "In Progress";
    case "completed":
    case "complete":
    case "done":
      return "Completed";
    case "overdue":
    case "late":
    case "blocked":
      return "Overdue";
    case "not started":
    case "not_started":
    case "not-started":
    default:
      return "Not Started";
  }
};

const buildSafeUploadName = (originalName: string): string => {
  const name = String(originalName || "").trim();
  const dotIndex = name.lastIndexOf(".");
  const ext = dotIndex > -1 ? name.slice(dotIndex) : "";
  const safeExt = /^[.][A-Za-z0-9]+$/.test(ext) ? ext.toLowerCase() : "";
  return `task_upload_${Date.now()}_${Math.random().toString(36).slice(2, 8)}${safeExt}`;
};

const TaskListPage: React.FC<any> = (props) => {
  const history = useHistory();
  const [mockData, setMockData] = useState<any[]>([]);
  const [myRawData, setMyRawData] = useState<any[]>([]);
  const [allRawData, setAllRawData] = useState<any[]>([]);
  const [loadingMine, setLoadingMine] = useState<boolean>(false);
  const [loadingAll, setLoadingAll] = useState<boolean>(false);
  const [hasLoadedAll, setHasLoadedAll] = useState<boolean>(false);
  const [showOnlyRelated, setShowOnlyRelated] = useState<boolean>(true);
  const loadingAllRef = useRef<boolean>(false);
  const currentUserId = parseInt(String(getUserId() || ""), 10);
  const [confirmedTasks, setConfirmedTasks] = useState<Set<string>>(new Set());
  const [completeModalVisible, setCompleteModalVisible] = useState(false);
  const [selectedTaskForComplete, setSelectedTaskForComplete] = useState<any>(null);
  const [completeSelection, setCompleteSelection] = useState<string>("task"); // "task" or checkpointId
  const [notificationModalVisible, setNotificationModalVisible] = useState(false);
  const [selectedTaskForNotification, setSelectedTaskForNotification] = useState<any>(null);

  // Progress Modal
  const [progressModalVisible, setProgressModalVisible] = useState(false);
  const [progressTask, setProgressTask] = useState<any>(null);
  const [progressType, setProgressType] = useState<string>("Routine");
  const [progressContent, setProgressContent] = useState("");
  const [progressFileList, setProgressFileList] = useState<any[]>([]);

  const fetchMine = useCallback(async () => {
    if (USE_MOCK) return;
    if (!currentUserId || isNaN(currentUserId) || currentUserId <= 0) {
      setLoadingMine(false);
      return;
    }
    try {
      setLoadingMine(true);
      const result = await ApiRequest({
        otherUrl: "TaskManagement/GetAllTasks",
        type: "get",
        params: { employeeId: currentUserId },
        isShowSpinner: true,
      });
      // 后端返回格式：Ok(TaskResponse<List<GetTaskDto>>)
      // 所以结构是：result.data.data (Ok包装) -> TaskResponse.Data
      console.log("TaskListPage: API response received", result);
      const responseData = result?.data?.data ?? result?.data;
      const data = Array.isArray(responseData) ? responseData : (responseData?.data ?? []);
      console.log("TaskListPage: Parsed data", data);
      setMyRawData(data || []);
    } catch (error: any) {
      console.error("TaskListPage: Failed to load my tasks", error);
      console.error("TaskListPage: Error details", {
        message: error?.message,
        response: error?.response,
        status: error?.response?.status,
        data: error?.response?.data
      });
      const errorMsg = error?.response?.data?.errorMessage || error?.message || "Failed to load my tasks";
      message.error(errorMsg);
      setMyRawData([]); // 设置空数组，避免一直 loading
    } finally {
      console.log("TaskListPage: fetchMine completed, setting loadingMine to false");
      setLoadingMine(false);
    }
  }, [currentUserId]);

  const fetchAll = useCallback(async () => {
    if (USE_MOCK) return;
    if (loadingAllRef.current) return;
    loadingAllRef.current = true;
    try {
      setLoadingAll(true);
      const result = await ApiRequest({
        otherUrl: "TaskManagement/GetAllTasksAdmin",
        type: "get",
        isShowSpinner: false,
      });
      // 后端返回格式：Ok(TaskResponse<List<GetTaskDto>>)
      console.log("TaskListPage: GetAllTasksAdmin response received", result);
      const responseData = result?.data?.data ?? result?.data;
      const data = Array.isArray(responseData) ? responseData : (responseData?.data ?? []);
      console.log("TaskListPage: GetAllTasksAdmin parsed data", data);
      setAllRawData(data || []);
      setHasLoadedAll(true);
    } catch (error: any) {
      console.error("TaskListPage: Failed to load all tasks", error);
      console.error("TaskListPage: Error details", {
        message: error?.message,
        response: error?.response,
        status: error?.response?.status,
        data: error?.response?.data
      });
      const errorMsg = error?.response?.data?.errorMessage || error?.message || "Failed to load all tasks";
      message.error(errorMsg);
      setAllRawData([]); // 设置空数组，避免一直 loading
    } finally {
      console.log("TaskListPage: fetchAll completed, setting loadingAll to false");
      setLoadingAll(false);
      loadingAllRef.current = false;
    }
  }, []);

  const refreshTasks = useCallback(async () => {
    try {
      await fetchMine();
      if (hasLoadedAll || !showOnlyRelated) {
        await fetchAll();
      }
    } catch (error: any) {
      console.error("Failed to refresh tasks", error);
      // 错误已经在 fetchMine/fetchAll 中处理了，这里只记录日志
    }
  }, [fetchMine, fetchAll, hasLoadedAll, showOnlyRelated]);

  const handleCompleteClick = useCallback(
    (row: any) => {
      // 先获取完整的任务数据（包括 checkpoints）
      const taskId = row?.id ? parseInt(String(row.id), 10) : 0;
      if (!taskId) {
        message.error("Task id is missing.");
        return;
      }
      
      // 设置选中的任务并显示 Modal
      setSelectedTaskForComplete(row);
      setCompleteSelection("task"); // 默认选择"完成整个任务"
      setCompleteModalVisible(true);
    },
    []
  );

  const handleCompleteConfirm = useCallback(
    async () => {
      if (!selectedTaskForComplete) {
        return;
      }

      try {
        const createdByRaw = getUserId();
        const createdBy = createdByRaw ? parseInt(String(createdByRaw), 10) : 0;
        if (!createdBy) {
          throw new Error("Current user is not identified.");
        }
        const taskId = selectedTaskForComplete?.id ? parseInt(String(selectedTaskForComplete.id), 10) : 0;
        if (!taskId) {
          throw new Error("Task id is missing.");
        }
        const notifyIds = (selectedTaskForComplete.participants || [])
          .map((p: any) => p?.userId ?? p?.UserId ?? 0)
          .map((id: any) => parseInt(String(id), 10))
          .filter((id: any) => Number.isFinite(id) && id > 0);

        // 构建评论内容
        let commentContent = "";
        let shouldUpdateStatus = false;

        if (completeSelection === "task") {
          // 完成整个任务
          commentContent = "Task completed.";
          shouldUpdateStatus = true;
          
          // 更新任务状态
          await ApiRequest({
            otherUrl: "TaskManagement/UpdateTask",
            method: "put",
            params: { taskId },
            data: {
              title: selectedTaskForComplete.title || "",
              description: selectedTaskForComplete.description || "",
              status: "Completed",
            },
            isShowSpinner: true,
          });
        } else {
          // 完成特定 checkpoint
          const checkpointId = parseInt(completeSelection, 10);
          const checkpoint = (selectedTaskForComplete.checkpoints || []).find(
            (c: any) => (c.checkpointId === checkpointId) || (c.checkpointId === 0 && c.name === completeSelection)
          );
          const checkpointName = checkpoint?.name || (isNaN(checkpointId) ? completeSelection : `Checkpoint #${checkpointId}`);
          commentContent = `Checkpoint completed: ${checkpointName}`;
          shouldUpdateStatus = false;
          
          // 调用 CompleteCheckpoint API 更新 checkpoint 状态
          if (checkpoint?.checkpointId && checkpoint.checkpointId > 0) {
            await ApiRequest({
              otherUrl: `TaskManagement/CompleteCheckpoint`,
              method: "post",
              params: { checkpointId: checkpoint.checkpointId, completedBy: createdBy },
              isShowSpinner: true,
            });
          }
        }

        // 添加评论
        await ApiRequest({
          otherUrl: "TaskComment/AddComment",
          method: "post",
          data: {
            taskId,
            createdBy,
            commentType: "Complete",
            content: commentContent,
            notifyUserIds: notifyIds,
          },
          isShowSpinner: false,
        });

        message.success(shouldUpdateStatus ? "Task marked as Completed" : "Checkpoint completed");
        setCompleteModalVisible(false);
        setSelectedTaskForComplete(null);
        refreshTasks().catch((err) => {
          console.error("Failed to refresh tasks", err);
        });
      } catch (error: any) {
        message.error(error?.message || "Complete failed");
      }
    },
    [selectedTaskForComplete, completeSelection, refreshTasks]
  );

  useEffect(() => {
    document.title = "Task List";
  }, []);

  useEffect(() => {
    if (USE_MOCK) {
      const mockTasks = Array.from({ length: 10 }, (_, i) => ({
        id: i + 1,
        title: `Task ${i + 1}: ${["开发新功能", "修复Bug", "优化性能", "代码审查"][i % 4]}`,
        description: `这是任务 ${i + 1} 的描述`,
        manager: ["Alice", "Bob", "Charlie"][i % 3],
        managerName: ["Alice", "Bob", "Charlie"][i % 3],
        assignees: ["David", "Emma"].slice(0, (i % 2) + 1),
        followers: ["Frank", "Grace"],
        startDate: moment().subtract(i, "days").format("YYYY-MM-DD"),
        dueDate: moment().add(7 - i, "days").format("YYYY-MM-DD"),
        status: ["Not Started", "In Progress", "Completed", "Overdue"][i % 4],
        comments: [
          {
            commentType: "Routine",
            content: `最新进展更新 ${i + 1}`,
            createdBy: "User" + (i % 3),
            createdAt: moment().subtract(i, "hours").toISOString(),
          },
        ],
        checklistStatus: i % 5 === 0 ? "Overdue" : "Normal",
        participants: [
          { userId: 1, name: "Alice", role: "manager" },
          { userId: 2, name: "Bob", role: "assignee" },
        ],
      }));
      setMockData(mockTasks);
    } else {
      console.log("TaskListPage: Fetching tasks, currentUserId:", currentUserId);
      fetchMine().catch((error: any) => {
        console.error("TaskListPage: Failed to fetch tasks in useEffect", error);
        // 错误已经在 fetchMine 内部处理了，这里只记录日志
      });
    }
  }, [fetchMine, currentUserId]);

  const mapTasks = useCallback(
    (rows: any[]) =>
      (rows || []).map((r) => {
        const comments: TaskCommentInfo[] = (r.comments ?? r.Comments ?? []).map(
          (c: any): TaskCommentInfo => ({
            commentType: c?.commentType ?? c?.CommentType ?? "",
            content: c?.content ?? c?.Content ?? "",
            createdBy: c?.createdBy ?? c?.CreatedBy ?? "",
            createdAt: c?.createdAt ?? c?.CreatedAt ?? "",
          })
        );
        const sortedComments = comments.sort(
          (a, b) => (toNZTime(b.createdAt)?.valueOf() || 0) - (toNZTime(a.createdAt)?.valueOf() || 0)
        );

        const participants: TaskParticipant[] = (r.participants ?? r.Participants ?? []).map(
          (p: any): TaskParticipant => ({
            userId: parseInt(String(p?.userId ?? p?.UserId ?? 0), 10) || 0,
            name: p?.name ?? p?.Name ?? "",
            role: (p?.role ?? p?.Role ?? "").toLowerCase(),
          })
        );

        const isRelated = currentUserId
          ? participants.some((p) => p.userId === currentUserId)
          : false;

        // 获取 manager 名称：优先使用后端返回的 ManagerName，如果没有则从 participants 中查找
        const managerFromParticipants = participants.find(
          (p) => p.role === "manager" && p.name
        );
        const managerName = 
          r.managerName ?? 
          r.ManagerName ?? 
          r.manager ?? 
          r.Manager ?? 
          managerFromParticipants?.name ?? 
          "-";

        const originalStatus = normalizeStatusLabel(r.status ?? r.Status) as Status;
        const startDate = r.startDate ?? r.StartDate ?? null;
        const dueDate = r.dueDate ?? r.DueDate ?? null;
        const checkpoints = r.checkpoints ?? r.Checkpoints ?? [];
        
        // 根据 checkpoints 计算状态（新逻辑）
        const computedStatus = calculateStatus(originalStatus, startDate, dueDate, checkpoints);
        
        // 根据计算后的状态获取颜色
        const statusColor = getStatusColor(computedStatus);
        
        // 调试日志
        if (process.env.NODE_ENV !== "production") {
          console.log("TaskListPage: Status calculation", {
            id: r.id ?? r.taskId ?? r.TaskId,
            originalStatus,
            computedStatus,
            statusColor,
            startDate,
            dueDate,
            now: moment().tz(NZ_TIMEZONE).format("YYYY-MM-DD"),
          });
        }

        const taskId = String(r.id ?? r.taskId ?? r.TaskId);
        
        // 检查确认状态：优先从本地状态，其次从参与者数据中检查，最后从评论中检查
        let myConfirmed = confirmedTasks.has(taskId);
        if (!myConfirmed && currentUserId) {
          // 从参与者数据中检查确认状态
          const myParticipant = participants.find((p) => p.userId === currentUserId);
          if (myParticipant && (myParticipant as any).isConfirmed) {
            myConfirmed = true;
          } else {
            // 如果没有参与者数据，检查是否有当前用户创建的确认评论
            // 确认评论通过 "routine" 类型的评论来记录，内容以 [CONFIRMED] 开头
            myConfirmed = sortedComments.some(
              (c: TaskCommentInfo) => {
                const isMyComment = c.createdBy === String(currentUserId) || parseInt(String(c.createdBy), 10) === currentUserId;
                if (!isMyComment) return false;
                
                // 检查评论内容是否以 [CONFIRMED] 开头（后端添加的确认标记）
                const content = c.content || "";
                return content.trim().startsWith("[CONFIRMED]");
              }
            );
          }
        }

        // 提取已确认用户ID列表（从后端返回的数据中）
        const confirmedUserIds: number[] = (r.confirmedUserIds ?? r.ConfirmedUserIds ?? []).map((id: any) =>
          parseInt(String(id), 10)
        ).filter((id: number) => !isNaN(id) && id > 0);

        return {
          ...r,
          id: taskId,
          title: r.title ?? r.Title ?? "",
          description: r.description ?? r.Description ?? "",
          manager: managerName,
          managerName: managerName,
          managerId: r.managerId ?? r.ManagerId ?? managerFromParticipants?.userId,
          assignees: (r.assignees ?? r.Assignees ?? []).map((a: any) =>
            typeof a === "string" ? a : a?.name ?? a
          ),
          followers: (r.followers ?? r.Followers ?? []).map((f: any) =>
            typeof f === "string" ? f : f?.name ?? f
          ),
          startDate: startDate,
          dueDate: dueDate,
          status: computedStatus,
          statusColor,
          comments: sortedComments,
          latestComment: sortedComments[0]
            ? `${normalizeTaskCommentDisplayType(
                sortedComments[0].commentType,
                sortedComments[0].content
              )}: ${sortedComments[0].content}`
            : undefined,
          participants,
          isRelated,
          myConfirmed,
          currentUserId,
          confirmedUserIds,
        };
      }),
    [currentUserId, confirmedTasks]
  );

  // 状态排序权重：Overdue > In Progress > Completed > Not Started
  const getStatusPriority = (status: string): number => {
    switch (status) {
      case "Overdue": return 0;
      case "In Progress": return 1;
      case "Completed": return 2;
      case "Not Started": return 3;
      default: return 4;
    }
  };

  // 排序函数：先按状态，再按截止日期
  const sortTasks = (tasks: any[]): any[] => {
    return [...tasks].sort((a, b) => {
      // 1. 先按状态优先级排序
      const statusPriorityA = getStatusPriority(a.status);
      const statusPriorityB = getStatusPriority(b.status);
      if (statusPriorityA !== statusPriorityB) {
        return statusPriorityA - statusPriorityB;
      }
      // 2. 状态相同时，按截止日期排序（早的在前）
      const dueDateA = a.dueDate ? toNZTime(a.dueDate)?.valueOf() || 0 : Number.MAX_SAFE_INTEGER;
      const dueDateB = b.dueDate ? toNZTime(b.dueDate)?.valueOf() || 0 : Number.MAX_SAFE_INTEGER;
      return dueDateA - dueDateB;
    });
  };

  const mineData = useMemo(() => {
    if (USE_MOCK) return mockData;
    const mapped = mapTasks(myRawData).filter((row: any) => row.isRelated);
    return sortTasks(mapped);
  }, [mockData, myRawData, mapTasks]);

  const allData = useMemo(() => {
    if (USE_MOCK) return mockData;
    const mapped = mapTasks(allRawData);
    return sortTasks(mapped);
  }, [mockData, allRawData, mapTasks]);

  const displayData = showOnlyRelated ? mineData : allData;
  const isLoading = USE_MOCK ? false : showOnlyRelated ? loadingMine : loadingAll;
  
  // 调试日志 - 监控加载状态
  useEffect(() => {
    console.log("TaskListPage: State update", {
      USE_MOCK,
      showOnlyRelated,
      loadingMine,
      loadingAll,
      isLoading,
      currentUserId,
      myRawDataLength: myRawData.length,
      allRawDataLength: allRawData.length,
      displayDataLength: displayData.length
    });
  }, [USE_MOCK, showOnlyRelated, loadingMine, loadingAll, isLoading, currentUserId, myRawData.length, allRawData.length, displayData.length]);

  const handleViewChange = useCallback(
    (value: string) => {
      const mine = value === "mine";
      setShowOnlyRelated(mine);
      if (!mine && !hasLoadedAll) {
        fetchAll();
      }
    },
    [fetchAll, hasLoadedAll]
  );

  const isManagerForRow = useCallback(
    (row: any) => {
      if (!row || !currentUserId || currentUserId <= 0) return false;
      const managerIdRaw = row.managerId ?? row.ManagerId;
      const managerId = managerIdRaw ? parseInt(String(managerIdRaw), 10) : 0;
      if (Number.isFinite(managerId) && managerId > 0 && managerId === currentUserId) {
        return true;
      }
      const participants: TaskParticipant[] = row.participants || [];
      return participants.some(
        (p) =>
          p.userId === currentUserId &&
          String(p.role ?? "").toLowerCase() === "manager"
      );
    },
    [currentUserId]
  );

  const commonActionButtons = useMemo(
    () =>
      [
        {
          icon: () => <EyeOutlined />,
          tooltip: "Detail",
          buttonLabel: "Detail",
          buttonColor: "#1976D2",
          onClick: (_: any, row: any) => {
            history.push(`/tasks/detail/${row.id}`);
          },
        },
        {
          isFreeAction: true,
          icon: () => <EditOutlined />,
          tooltip: "New Task",
          onClick: () => history.push("/tasks/new"),
        },
      ] as any[],
    [history]
  );

  const handleDeleteTask = useCallback(async (row: any) => {
    try {
      const taskId = parseInt(row.id, 10);
      if (!taskId || isNaN(taskId)) {
        message.error("Invalid task ID");
        return;
      }

      const confirmed = await SweetAlertService.confirmMessage(
        `Are you sure you want to delete task "${row.title || ""}"?`
      );

      if (!confirmed) {
        return;
      }

      await ApiRequest({
        otherUrl: `TaskManagement/DeleteTask?taskId=${taskId}`,
        method: "delete",
        isShowSpinner: true,
      });

      message.success("Task deleted successfully");
      refreshTasks().catch((err) => {
        console.error("Failed to refresh tasks after deletion", err);
      });
    } catch (error: any) {
      console.error("Failed to delete task", error);
      const errorMsg =
        error?.response?.data?.errorMessage ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete task";
      message.error(errorMsg);
    }
  }, [refreshTasks]);

  const openProgressModal = useCallback((row: any) => {
    setProgressTask(row);
    setProgressType("Routine");
    setProgressContent("");
    setProgressFileList([]);
    setProgressModalVisible(true);
  }, []);

  const handleProgressSubmit = useCallback(async () => {
    if (!progressTask) return;
    if (!progressContent.trim()) {
      message.warning("Please enter content");
      return;
    }
    const taskId = parseInt(progressTask.id, 10);
    if (!taskId || isNaN(taskId)) {
      message.error("Invalid task ID");
      return;
    }
    const creatorRaw = getUserId();
    const createdBy = creatorRaw ? parseInt(String(creatorRaw), 10) : 0;
    if (!createdBy) {
      message.error("Current user is not identified.");
      return;
    }
    try {
      // 上传附件
      const uploadedAttachments: { fileUrl: string; fileName: string; fileType: string }[] = [];
      if (progressFileList.length > 0) {
        message.loading({ content: "Uploading attachments...", key: "progress-upload" });
        for (const fileItem of progressFileList) {
          if (fileItem.originFileObj) {
            try {
              const formData = new FormData();
              formData.append(
                "imageFile",
                fileItem.originFileObj,
                buildSafeUploadName(fileItem.name || fileItem.originFileObj?.name || "file")
              );
              const uploadRes = await ApiRequest({
                otherUrl: "Common/UploadImage",
                method: "post",
                data: formData,
                isShowSpinner: false,
              });
              const fileUrl = uploadRes?.data?.data ?? uploadRes?.data ?? uploadRes;
              if (typeof fileUrl === "string" && fileUrl) {
                uploadedAttachments.push({
                  fileUrl,
                  fileName: fileItem.name,
                  fileType: fileItem.type || "unknown",
                });
              }
            } catch (e) {
              console.error("Failed to upload file:", fileItem.name, e);
            }
          }
        }
        message.destroy("progress-upload");
      }

      const notifyIds = (progressTask.participants || [])
        .map((p: any) => p.userId ?? p.UserId)
        .filter((id: any) => !!id)
        .map((id: any) => parseInt(String(id), 10));

      await ApiRequest({
        otherUrl: "TaskComment/AddComment",
        method: "post",
        data: {
          taskId,
          createdBy,
          commentType: taskCommentTypeForApi(progressType),
          content: progressContent.trim(),
          notifyUserIds: notifyIds,
          attachments: uploadedAttachments.length > 0 ? uploadedAttachments : null,
        },
        isShowSpinner: true,
      });

      message.success("Comment added successfully");
      setProgressModalVisible(false);
      setProgressTask(null);
      refreshTasks().catch(() => {});
    } catch (error: any) {
      console.error("Failed to add progress comment", error);
      message.error(error?.message || "Failed to add comment");
    }
  }, [progressTask, progressType, progressContent, progressFileList, refreshTasks]);

  const handleMyConfirm = useCallback(async (row: any) => {
    try {
      const taskId = parseInt(row.id, 10);
      if (!taskId || isNaN(taskId)) {
        message.error("Invalid task ID");
        return;
      }

      if (!currentUserId || isNaN(currentUserId) || currentUserId <= 0) {
        message.error("User not identified");
        return;
      }

      // 调用后端 API 保存确认状态
      await ApiRequest({
        otherUrl: "TaskManagement/ConfirmTask",
        method: "post",
        data: {
          taskId: taskId,
          confirmedBy: currentUserId,
          comment: `User confirmed task: ${row.title || ""}`,
        },
        isShowSpinner: true,
      });

      // 更新本地状态
      setConfirmedTasks((prev) => {
        const next = new Set(prev);
        next.add(row.id);
        return next;
      });

      message.success("Confirmed");
      
      // 刷新任务列表以获取最新数据
      try {
        if (showOnlyRelated) {
          await fetchMine();
        } else {
          await fetchAll();
        }
      } catch (refreshError: any) {
        console.error("Failed to refresh task list after confirmation", refreshError);
        // 不显示错误，因为确认已经成功了
      }
    } catch (error: any) {
      console.error("Failed to confirm task", error);
      console.error("Error details:", {
        response: error?.response,
        data: error?.response?.data,
        status: error?.response?.status,
        message: error?.message,
      });
      const errorMsg = 
        error?.response?.data?.errorMessage || 
        error?.response?.data?.message ||
        error?.message || 
        "Failed to confirm task";
      message.error(errorMsg);
    }
  }, [currentUserId, showOnlyRelated, fetchMine, fetchAll]);

  const relatedActionButtons = useMemo(
    () =>
      [
        {
          icon: () => <EyeOutlined />,
          tooltip: "Detail",
          buttonLabel: "Detail",
          buttonColor: "#1976D2",
          onClick: (_: any, row: any) => {
            history.push(`/tasks/detail/${row.id}`);
          },
        },
        {
          icon: () => <EditOutlined />,
          tooltip: "Edit",
          buttonLabel: "Edit",
          buttonColor: "#4CAF50",
          hidden: (row: any) =>
            row && (row.isRelated === false || !isManagerForRow(row)),
          onClick: (_: any, row: any) => {
            history.push(`/tasks/${row.id}/edit`);
          },
        },
        {
          icon: () => <CommentOutlined />,
          tooltip: "Progress",
          buttonLabel: "Progress",
          buttonColor: "#FFC107",
          buttonTextColor: "#1F1F1F",
          hidden: (row: any) => row && row.isRelated === false,
          onClick: (_: any, row: any) => {
            openProgressModal(row);
          },
        },
        {
          icon: () => <CheckCircleOutlined />,
          tooltip: "Complete",
          buttonLabel: "Complete",
          buttonColor: "#673AB7",
          hidden: (row: any) =>
            row && (row.isRelated === false || !isManagerForRow(row)),
          onClick: (_: any, row: any) => {
            handleCompleteClick(row);
          },
        },
        {
          icon: () => <NotificationOutlined />,
          tooltip: "Notice",
          buttonLabel: "Notice",
          buttonColor: "#F44336",
          hidden: (row: any) => row && row.isRelated === false,
          onClick: (_: any, row: any) => {
            setSelectedTaskForNotification(row);
            setNotificationModalVisible(true);
          },
        },
        {
          icon: () => <CheckCircleOutlined />,
          tooltip: "Confirm",
          buttonLabel: "Confirm",
          buttonColor: "#ff7a00",
          alignLeft: true, // 标记为左侧按钮
          hidden: (row: any) => row && (row.isRelated === false || row.myConfirmed === true),
          onClick: (_: any, row: any) => {
            handleMyConfirm(row);
          },
        },
        {
          icon: () => <DeleteOutlined />,
          tooltip: "Delete",
          buttonLabel: "Delete",
          buttonColor: "#ff4d4f",
          hidden: (row: any) => row && (!isManagerForRow(row) || row.isRelated === false),
          onClick: (_: any, row: any) => {
            if (!isManagerForRow(row)) {
              SweetAlertService.warningMessage("Only managers can delete this task.", "Warning");
              return;
            }
            handleDeleteTask(row);
          },
        },
        {
          isFreeAction: true,
          icon: () => <EditOutlined />,
          tooltip: "New Task",
          onClick: () => history.push("/tasks/new"),
        },
      ] as any[],
    [handleCompleteClick, isManagerForRow, history, handleMyConfirm, handleDeleteTask, openProgressModal]
  );

  // 检测屏幕尺寸，用于响应式设计
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1024);
  
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={{ width: '100%', boxSizing: 'border-box' }}>
      <div
        style={{
          marginBottom: 12,
          display: "flex",
          justifyContent: isSmallScreen ? "flex-start" : "flex-end",
          alignItems: "center",
          flexWrap: 'wrap', // 允许换行
          gap: 8
        }}
      >
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          gap: 8,
          fontSize: isSmallScreen ? '0.875rem' : '1rem'
        }}>
          <span>Show:</span>
          <select
            value={showOnlyRelated ? "mine" : "all"}
            onChange={(e) => handleViewChange(e.target.value)}
            style={{ 
              padding: isSmallScreen ? "4px 6px" : "6px 8px",
              fontSize: isSmallScreen ? '0.875rem' : '1rem'
            }}
          >
            <option value="mine">My Tasks Only</option>
            <option value="all">All Tasks</option>
          </select>
        </div>
      </div>
      {isLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
          <Spin />
        </div>
      ) : (
        <CommonTableTwoLine
          title={props.title || "Task List"}
          column={TaskListColumnModel()}
          initData={displayData}
          editable={false}
          isNotAddable={true}
          isNotEditable={true}
          isNotDeletable={true}
          actionButtons={showOnlyRelated ? relatedActionButtons : commonActionButtons}
          defaultPageSize={10}
          defaultPageSizeOptions={[10, 20, 50]}
          rowStyle={(row: any) => {
            return {};
          }}
        />
      )}

      {/* Notification Modal */}
      <TaskNotificationModal
        visible={notificationModalVisible}
        taskId={selectedTaskForNotification?.id || null}
        taskInfo={selectedTaskForNotification}
        onClose={() => {
          setNotificationModalVisible(false);
          setSelectedTaskForNotification(null);
        }}
        onSuccess={() => {
          refreshTasks().catch((err) => {
            console.error("Failed to refresh tasks", err);
          });
        }}
      />

      {/* Complete Task Modal */}
      <Modal
        title="Complete Task"
        visible={completeModalVisible}
        onOk={handleCompleteConfirm}
        onCancel={() => {
          setCompleteModalVisible(false);
          setSelectedTaskForComplete(null);
          setCompleteSelection("task");
        }}
        okText="Complete"
        cancelText="Cancel"
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 12, fontWeight: 600 }}>
            Select what to complete:
          </div>
          <Radio.Group
            value={completeSelection}
            onChange={(e) => setCompleteSelection(e.target.value)}
          >
            <Space direction="vertical">
              <Radio value="task">
                <strong>Complete entire task</strong>
              </Radio>
              {selectedTaskForComplete?.checkpoints && selectedTaskForComplete.checkpoints.length > 0 && (
                <>
                  {selectedTaskForComplete.checkpoints.map((cp: any) => (
                    <Radio key={cp.checkpointId || cp.name} value={String(cp.checkpointId || cp.name)}>
                      Complete checkpoint: <strong>{cp.name}</strong>
                    </Radio>
                  ))}
                </>
              )}
            </Space>
          </Radio.Group>
        </div>
      </Modal>

      {/* Progress Modal */}
      <Modal
        title={`Progress Update${progressTask ? ` - Task ID: ${progressTask.id}` : ""}`}
        visible={progressModalVisible}
        onOk={handleProgressSubmit}
        onCancel={() => {
          setProgressModalVisible(false);
          setProgressTask(null);
        }}
        okText="Add Comment"
        cancelText="Cancel"
        width={640}
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 600 }}>Type <span style={{ color: "red" }}>*</span></div>
          <Select
            value={progressType}
            onChange={(val: string) => setProgressType(val)}
            style={{ width: "100%" }}
          >
            <Select.Option value="Routine">Routine</Select.Option>
            <Select.Option value="Apply Reschedule">Apply Reschedule</Select.Option>
            <Select.Option value="Apply Completion">Apply Completion</Select.Option>
          </Select>
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 600 }}>Content <span style={{ color: "red" }}>*</span></div>
          <Input.TextArea
            rows={6}
            placeholder="Enter progress update..."
            value={progressContent}
            onChange={(e) => setProgressContent(e.target.value)}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8 }}>Attachment</div>
          <Upload
            multiple
            fileList={progressFileList}
            beforeUpload={() => false}
            onChange={({ fileList }) => setProgressFileList(fileList)}
            onRemove={(file) => {
              setProgressFileList((prev) => prev.filter((f) => f.uid !== file.uid));
              return true;
            }}
            accept=".jpg,.jpeg,.png,.pdf,.xls,.xlsx,.doc,.docx"
          >
            <Button icon={<UploadOutlined />}>Select File</Button>
          </Upload>
        </div>
        {progressTask && (
          <div style={{ background: "#fafafa", border: "1px solid #d9d9d9", borderRadius: 4, padding: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Notification</div>
            <Typography.Text type="secondary">All Task Members</Typography.Text>
            <div>
              <Typography.Text>
                {progressTask.title || `Task #${progressTask.id}`} Progress Update: {progressType} comment is added.
              </Typography.Text>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TaskListPage;
